import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { socialPosts } from "../constants/social.js";
import { catalogueEntries, fetchChannelVideos, selectVideos } from "../utils/youtubeFeed.js";
import { ORG } from "../seo/site.js";
import { langOf } from "../utils/lang.js";
import { trackEvent } from "../utils/analytics.js";
import leaf from "../assets/Images/leaf.png";

// Homepage "From our school Media Box" section. Videos come live from the WSTSC
// YouTube channel (src/utils/youtubeFeed.js), filtered by the settings in
// src/constants/social.js. The saved channel catalogue renders first and stays
// if the feed can't be reached. The player itself loads only when a visitor
// presses play (youtube-nocookie.com), which keeps the homepage fast and private.

const thumb = (id, size) => `https://i.ytimg.com/vi/${id}/${size}.jpg`;

// YouTube's hq/sd thumbnails are 4:3 with black bars; object-fit: cover in a
// 16:9 box crops the bars away, so all three sizes can share one srcset.
const thumbSrcSet = (id) =>
  `${thumb(id, "mqdefault")} 320w, ${thumb(id, "hqdefault")} 480w, ${thumb(id, "sddefault")} 640w`;

const formatDate = (isoDate) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-AU", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const watchUrl = (id) => `https://www.youtube.com/watch?v=${id}`;

const Thumbnail = ({ video, sizes, className = "", eager = false }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null; // the dark placeholder behind it stays visible
  return (
    <img
      src={thumb(video.id, "hqdefault")}
      srcSet={thumbSrcSet(video.id)}
      sizes={sizes}
      alt={video.thumbnailAlt}
      width="480"
      height="270"
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className={`w-100 h-100 object-fit-cover ${className}`}
      onError={() => setFailed(true)}
    />
  );
};

// Up/down sliding list: a window exactly VISIBLE_ITEMS tall that scrolls one
// video per arrow press. Wheel, touch and keyboard scrolling also work and
// snap to items (see .social-video-list in Style.css).
const VISIBLE_ITEMS = 4;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const VideoList = ({ videos, currentId, onPlay }) => {
  const listRef = useRef(null);
  const [step, setStep] = useState(0); // one item + gap, in px
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);
  const slides = videos.length > VISIBLE_ITEMS;

  const updateEdges = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    setAtTop(list.scrollTop <= 1);
    setAtBottom(list.scrollTop + list.clientHeight >= list.scrollHeight - 1);
  }, []);

  // Measure the real layout: the window runs from the top of the 1st item to
  // the bottom of the 4th, and one step is the distance between two items.
  // Re-measure when an item resizes (screen width or font changes).
  const [windowHeight, setWindowHeight] = useState(0);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!slides || !list) return undefined;
    const measure = () => {
      const items = list.children;
      const first = items[0];
      const last = items[VISIBLE_ITEMS - 1];
      setStep(items[1].offsetTop - first.offsetTop);
      setWindowHeight(last.offsetTop + last.offsetHeight - first.offsetTop);
      updateEdges();
    };
    measure();
    if (!("ResizeObserver" in window)) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list.children[0]);
    return () => observer.disconnect();
  }, [slides, videos, updateEdges]);

  // Keep the video that is playing visible inside the list.
  useEffect(() => {
    const list = listRef.current;
    const item = list?.querySelector(`[data-video-id="${currentId}"]`);
    if (!slides || !item) return;
    const top = item.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, [currentId, slides]);

  const move = (direction) =>
    listRef.current?.scrollBy({
      top: direction * step,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });

  const arrow = (direction, disabled) => (
    <button
      type="button"
      className="social-list-arrow btn rounded-circle d-flex align-items-center justify-content-center mx-auto"
      onClick={() => move(direction)}
      disabled={disabled}
      aria-controls="community-video-list"
      aria-label={direction < 0 ? "Show previous videos" : "Show more videos"}
    >
      <i className={`bi bi-chevron-${direction < 0 ? "up" : "down"}`} aria-hidden="true"></i>
    </button>
  );

  return (
    <div className="d-flex flex-column gap-2">
      {slides && arrow(-1, atTop)}
      <ul
        id="community-video-list"
        ref={listRef}
        aria-label="More videos"
        onScroll={updateEdges}
        className={`list-unstyled d-flex flex-column gap-3 mb-0 ${slides ? "social-video-list" : ""}`}
        style={slides && windowHeight ? { height: `${windowHeight}px` } : undefined}
      >
        {videos.map((video) => {
          const isCurrent = video.id === currentId;
          return (
            <li key={video.id} data-video-id={video.id}>
              <button
                type="button"
                onClick={() => onPlay(video)}
                aria-current={isCurrent ? "true" : undefined}
                className={`social-video-item d-flex gap-3 align-items-start w-100 text-start bg-white border rounded-4 p-2 ${
                  isCurrent ? "is-current" : ""
                }`}
              >
                <span className="social-video-thumb ratio ratio-16x9 rounded-3 overflow-hidden flex-shrink-0">
                  <Thumbnail video={video} sizes="160px" />
                </span>
                <span className="d-flex flex-column py-1">
                  <span className="fw-semibold lh-sm social-video-title" lang={langOf(video.title)}>
                    {video.title}
                  </span>
                  <span className="small text-muted mt-1">
                    {isCurrent ? "Now showing" : <time dateTime={video.date}>{formatDate(video.date)}</time>}
                  </span>
                  <span className="visually-hidden">, play video</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {slides && arrow(1, atBottom)}
    </div>
  );
};

const followLinks = [
  { platform: "youtube", label: "Watch on YouTube", icon: "bi-youtube", href: ORG.social.youtube },
  { platform: "facebook", label: "Follow on Facebook", icon: "bi-facebook", href: ORG.social.facebook },
  { platform: "instagram", label: "Follow on Instagram", icon: "bi-instagram", href: ORG.social.instagram },
].filter((link) => link.href);

const platformName = { facebook: "Facebook", instagram: "Instagram" };

// The saved catalogue (channelVideos) goes through the same filters as the
// live feed, so hiding a video in social.js hides it everywhere.
const initialVideos = selectVideos(catalogueEntries());

const SocialSection = () => {
  const posts = socialPosts.filter((post) => !post.hidden);

  const [videos, setVideos] = useState(initialVideos);
  const [feedSource, setFeedSource] = useState("fallback");
  const [currentId, setCurrentId] = useState(initialVideos[0]?.id);
  const [playing, setPlaying] = useState(false);
  const sectionRef = useRef(null);
  const playerRef = useRef(null);
  const playingRef = useRef(false);
  const current = videos.find((video) => video.id === currentId) || videos[0];

  // Fetch the live channel feed once the section is close to the screen, so
  // visitors who never scroll this far don't make the request at all.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    let cancelled = false;

    const load = () => {
      fetchChannelVideos()
        .then((live) => {
          if (cancelled || live.length === 0) return;
          setVideos(live);
          setFeedSource("live");
          // Don't swap the featured video out from under someone watching it.
          if (!playingRef.current) setCurrentId(live[0].id);
        })
        .catch((error) => {
          // Keep the approved fallback videos; the section still works.
          console.warn("YouTube feed unavailable, showing saved videos:", error.message);
        });
    };

    if (!("IntersectionObserver" in window)) {
      load();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(section);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  const play = (video) => {
    setCurrentId(video.id);
    setPlaying(true);
    playingRef.current = true;
    trackEvent("video_play", { video_id: video.id, page_path: window.location.pathname });
    // Keep keyboard and screen-reader users with the player that just opened.
    requestAnimationFrame(() => {
      playerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      playerRef.current?.querySelector("iframe")?.focus();
    });
  };

  return (
    <section
      id="community"
      ref={sectionRef}
      data-feed-source={feedSource}
      className="social-section position-relative bg-white py-5"
      aria-labelledby="community-heading"
    >
      <div className="container position-relative py-5">
        <div className="mb-5 text-center d-flex flex-column align-items-center">
          <img src={leaf} alt="" style={{ height: "30px" }} />
          <h2
            id="community-heading"
            className="h1 fw-bold lh-base m-0"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            <span className="red-text">From our school </span>
            <span className="blue-text text-nowrap">Media Box</span>
          </h2>
          <p lang="ta" className="tamil-font fs-4 blue-text mb-2">
            எங்கள் பள்ளியின் ஊடகத் தொகுப்பிலிருந்து
          </p>
          <div className="red-bg rounded-pill" style={{ width: "70px", height: "4px" }}></div>
          <p className="text-muted mt-4 mb-0 col-lg-8">
            Tamil songs, stories and celebrations from our classes at Walters
            Road Public School. Watch what our students have been learning and
            follow WSTSC for class news and event highlights.
          </p>
        </div>

        {current && (
          <div className="row g-4 align-items-start">
            {/* Featured video */}
            <div className="col-lg-7">
              <div
                ref={playerRef}
                className="social-player ratio ratio-16x9 rounded-4 overflow-hidden shadow-sm"
              >
                {playing ? (
                  <iframe
                    key={current.id}
                    src={`https://www.youtube-nocookie.com/embed/${current.id}?autoplay=1&rel=0`}
                    title={`YouTube video: ${current.title}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    style={{ border: 0 }}
                  ></iframe>
                ) : (
                  <button
                    type="button"
                    className="social-play-btn border-0 p-0"
                    onClick={() => play(current)}
                    aria-label={`Play video: ${current.title}`}
                  >
                    <Thumbnail video={current} sizes="(min-width: 992px) 640px, 100vw" />
                    <span className="social-play-icon" aria-hidden="true">
                      <i className="bi bi-play-fill"></i>
                    </span>
                  </button>
                )}
              </div>

              <div className="mt-3">
                <p className="small text-muted mb-1">
                  <i className="bi bi-youtube social-yt-text me-1" aria-hidden="true"></i>
                  YouTube · <time dateTime={current.date}>{formatDate(current.date)}</time>
                </p>
                <h3 className="fs-4 fw-bold mb-2" lang={langOf(current.title)}>
                  {current.title}
                </h3>
                {current.summary && (
                  <p className="text-muted mb-2" lang={langOf(current.summary)}>
                    {current.summary}
                  </p>
                )}
                <a
                  href={watchUrl(current.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="red-text fw-semibold"
                >
                  Watch on YouTube
                  <span className="visually-hidden">: {current.title} (opens in a new tab)</span>
                </a>
                <p className="small text-muted mt-2 mb-0">
                  Videos load from YouTube only when you press play.
                </p>
              </div>
            </div>

            {/* Other videos: up/down sliding list, 4 visible at a time */}
            {videos.length > 1 && (
              <div className="col-lg-5">
                <VideoList videos={videos} currentId={current.id} onPlay={play} />
              </div>
            )}
          </div>
        )}

        {/* Facebook / Instagram posts - only when WSTSC has approved some */}
        {posts.length > 0 && (
          <div className="mt-5">
            <h3 className="fs-5 fw-bold mb-3">From Facebook and Instagram</h3>
            <div className="row g-4">
              {posts.map((post) => (
                <div className="col-sm-6 col-lg-3" key={post.url}>
                  <a
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-post-card d-block h-100 bg-white border rounded-4 overflow-hidden text-decoration-none text-dark"
                  >
                    <span className="ratio ratio-1x1 d-block bg-light">
                      <img
                        src={post.image}
                        alt={post.imageAlt}
                        loading="lazy"
                        decoding="async"
                        className="w-100 h-100 object-fit-cover"
                      />
                    </span>
                    <span className="d-block p-3">
                      <span className="small text-muted d-block mb-1">
                        <i className={`bi bi-${post.platform} me-1`} aria-hidden="true"></i>
                        {platformName[post.platform]}
                        {post.date && (
                          <>
                            {" · "}
                            <time dateTime={post.date}>{formatDate(post.date)}</time>
                          </>
                        )}
                      </span>
                      <span className="d-block fw-semibold" lang={langOf(post.caption)}>
                        {post.caption}
                      </span>
                      <span className="visually-hidden"> (opens {platformName[post.platform]} in a new tab)</span>
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow the official accounts */}
        <div className="social-follow mt-5 rounded-4 p-4 p-md-5 text-center">
          <p className="fw-bold fs-5 mb-3">Follow WSTSC for class news and event highlights</p>
          <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-center gap-3">
            {followLinks.map((link) => (
              <a
                key={link.platform}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn social-follow-btn social-follow-${link.platform} rounded-5 px-4 py-2 fw-semibold d-inline-flex align-items-center justify-content-center`}
                onClick={() => trackEvent("social_follow", { platform: link.platform })}
              >
                <i className={`bi ${link.icon} me-2`} aria-hidden="true"></i>
                {link.label}
                <span className="visually-hidden"> (opens in a new tab)</span>
              </a>
            ))}
          </div>
          <p className="small text-muted mt-3 mb-0">
            Coming to an event?{" "}
            <Link to="/events/" className="red-text fw-semibold">
              See upcoming events
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default SocialSection;
